import {
  FONT_FACES,
  NIGHT_THEME,
  PAPER_THEME,
  RADIUS,
  SPACE,
  STROKE,
  TYPE,
  type Theme,
} from "@festipal/shared";

/**
 * Application du thème au back-office.
 *
 * Les valeurs viennent du paquet partagé, comme dans l'application : une seule
 * définition, deux surfaces. Elles sont posées en variables CSS pour que la
 * feuille de style reste une feuille de style — et non un fichier de constantes
 * dupliquées, qui divergerait à la première retouche.
 */

function apply(theme: Theme) {
  const root = document.documentElement;

  for (const [name, value] of Object.entries(theme.inks)) {
    // `paperRaised` devient `--paper-raised` : la convention CSS, pas celle de
    // TypeScript.
    root.style.setProperty(
      `--${name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`,
      value,
    );
  }

  for (const [name, value] of Object.entries(TYPE)) {
    root.style.setProperty(`--type-${name}`, `${value}px`);
  }
  for (const [name, value] of Object.entries(SPACE)) {
    root.style.setProperty(`--space-${name}`, `${value}px`);
  }
  for (const [name, value] of Object.entries(RADIUS)) {
    root.style.setProperty(`--radius-${name}`, `${value}px`);
  }
  root.style.setProperty("--stroke-hairline", `${STROKE.hairline}px`);
  root.style.setProperty("--stroke-edge", `${STROKE.edge}px`);
  root.style.setProperty("--stroke-bold", `${STROKE.bold}px`);
  // Same faces as the app; the files are declared in styles.css (@font-face).
  root.style.setProperty(
    "--face-display",
    `"${FONT_FACES.display}", "Arial Narrow", sans-serif`,
  );
  root.style.setProperty(
    "--face-heading",
    `"${FONT_FACES.heading}", "Arial Narrow", sans-serif`,
  );

  // Indique au navigateur comment dessiner ce qu'il fournit lui-même :
  // ascenseurs, champs natifs, menus déroulants.
  root.style.setProperty("color-scheme", theme === NIGHT_THEME ? "dark" : "light");
}

/**
 * Suit le réglage du système.
 *
 * Comme dans l'application : quelqu'un qui a mis son ordinateur en sombre l'a
 * fait pour de bonnes raisons, et un réglage de plus serait un réglage de plus à
 * trouver.
 */
export function followSystemTheme(): void {
  const dark = window.matchMedia("(prefers-color-scheme: dark)");
  const resolve = () => apply(dark.matches ? NIGHT_THEME : PAPER_THEME);

  resolve();
  dark.addEventListener("change", resolve);
}
